<template>
    <form class="contact--form" @submit.prevent="handleSubmitEnrollmentForm">
        <div v-if="showLoadingModal" class="loading-modal">
            <div class="loading-content">
                <div class="spinner"></div>
                <p>{{ $t('SCHOOL_CONTACT_FORM.FORM.LOADING_MESSAGE') }}</p>
            </div>
        </div>
        <fieldset class="form-section" v-if="!isNewEnrollment && !enrollment" role="group" aria-labelledby="section-enrollment-select">
            <legend id="section-enrollment-select" class="section-title cursor-pointer" @click.prevent="toggleSection('enrollmentSelect')">
                <fluent-icon :icon="collapsed.enrollmentSelect ? 'chevron-right' : 'chevron-down'" size="16" />
                {{ $t('SCHOOL_CONTACT_FORM.FORM.ENROLLMENTS.LABEL') }}
            </legend>
            <div class="row items-end" v-show="!collapsed.enrollmentSelect">
                <div class="small-12 medium-9 columns">
                    <label>
                        <span class="label-text">{{ $t('SCHOOL_CONTACT_FORM.FORM.ENROLLMENTS.LABEL') }}</span>
                    </label>
                    <multiselect
                        v-model="enrollment"
                        track-by='id'
                        label="student_name"
                        :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.ENROLLMENTS.PLACEHOLDER')"
                        selected-label=""
                        select-label=""
                        deselect-label=""
                        :max-height="160"
                        :options="enrollments"
                        :allow-empty="true"
                        :option-height="104"
                        @select="chooseEnrollment"
                    >
                        <template slot="option" slot-scope="{ option }">
                            <div
                                class="flex items-center justify-between w-full"
                                v-tooltip.right-start="{
                                    content: 'Aluno: ' + option.student_name + ' • Ano: ' + (option.school_year || '-'),
                                    delay: { show: 250, hide: 0 },
                                    hideOnTargetClick: false,
                                    autoHide: true,
                                    classes: 'tooltip-noninteractive',
                                }"
                            >
                                <span class="label-text">{{ option.student_name }}</span>
                                <span v-if="option && option.school_year" :class="getYearBadgeClass(option.school_year)">{{ option.school_year }}</span>
                            </div>
                        </template>
                        </multiselect>
                </div>
                <div class="small-12 medium-3 columns add-button-area">
                    <div class="add-button">
                        <woot-button size="small" variant="link" icon="add" @click="newEnrollmentButtonToggle">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.NEW_ENROLLMENT.LABEL') }}
                        </woot-button>
                    </div>
                </div>
            </div>
        </fieldset>

            <div v-if="enrollment || isNewEnrollment" class="medium-12 columns enrollment-title">
                <div class="flex flex-col gap-1">
                    <div v-if="isNewEnrollment" class="flex items-center gap-2">
                        <span class="page-sub-title section-title text-base font-semibold">
                            {{$t('SCHOOL_CONTACT_FORM.FORM.NEW_ENROLLMENT.TITLE') }}
                        </span>
                    </div>
                    <div v-else class="medium-12 column gap-1">
                        <div class="flex flex-col gap-1">
                            <span class="page-sub-title section-title text-base font-semibold">
                                {{`${$t('SCHOOL_CONTACT_FORM.FORM.EDIT_ENROLLMENT.TITLE')}`}}
                            </span>
                            <div class="flex items-center gap-2">
                                <span class="page-sub-title text-base font-semibold">
                                    {{`${enrollment.student_name}`}}
                                </span>
                                <span
                                    v-if="enrollment && (schoolYear || enrollment.school_year)"
                                    :class="getYearBadgeClass(schoolYear || enrollment.school_year)"
                                    v-tooltip.right-start="{
                                        content: 'Aluno: ' + enrollment.student_name + ' • Ano: ' + (schoolYear || enrollment.school_year),
                                        delay: { show: 300, hide: 0 },
                                        hideOnClick: true,
                                    }"
                                >
                                    {{ schoolYear || enrollment.school_year }}
                                </span>
                                <fluent-icon v-if="isDefaulterResponsibleWithSchool"
                                    v-tooltip.right-start="$t('SCHOOL_CONTACT_FORM.FORM.DEFAUTER_IN_SCHOOL_ALERT')"
                                    icon="info"
                                    size="22"
                                    class="pb-1 text-red-700"
                                />
                                <fluent-icon v-if="isDefaulterResponsibleWithKedu"
                                    v-tooltip.right-start="$t('SCHOOL_CONTACT_FORM.FORM.DEFAUTER_IN_KEDU_ALERT')"
                                    icon="info"
                                    size="22"
                                    class="pb-1 text-yellow-300"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="flex items-center gap-2 mt-2 flex-wrap">
                    <woot-button v-tooltip="$t('SCHOOL_CONTACT_FORM.FORM.SEARCH_OVERDUE_BILL.TOOLTIP')" v-if="enrollment && !isNewEnrollment"  @click.prevent="handleSearchOverdueBill">
                        {{ $t('SCHOOL_CONTACT_FORM.FORM.SEARCH_OVERDUE_BILL.LABEL') }}
                    </woot-button>

                    <woot-button v-tooltip="$t('SCHOOL_CONTACT_FORM.FORM.SYNC_WITH_KECUORE.TOOLTIP')" v-if="enrollment && !isNewEnrollment"  @click.prevent="syncWithKeducore">
                        {{ $t('SCHOOL_CONTACT_FORM.FORM.SYNC_WITH_KECUORE.LABEL') }}
                    </woot-button>

                    <woot-button
                        v-if="enrollment && !isNewEnrollment"
                        v-tooltip="$t('SCHOOL_CONTACT_FORM.FORM.DELETE_ENROLLMENT.TOOLTIP')"
                        size="small"
                        variant="smooth"
                        color-scheme="alert"
                        icon="delete"
                        :disabled="!canDeleteEnrollment || uiFlags.isDeleting"
                        :isLoading="uiFlags.isDeleting"
                        @click.prevent="openDeleteEnrollmentModal"
                    >
                        {{ $t('SCHOOL_CONTACT_FORM.FORM.DELETE_ENROLLMENT.LABEL') }}
                    </woot-button>
                </div>
            </div>

            <fieldset v-if="enrollment || isNewEnrollment" class="form-section" role="group" aria-labelledby="section-school">
                <legend id="section-school" class="section-title cursor-pointer" @click.prevent="toggleSection('school')">
                    <fluent-icon :icon="collapsed.school ? 'chevron-right' : 'chevron-down'" size="16" />
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_ACCOUNT_SELECT.LABEL') }}
                </legend>
                <div v-show="!collapsed.school">
                    <div class="row">
                        <label class="w-full">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_ACCOUNT_SELECT.LABEL') }}
                        </label>
                        <multiselect
                            v-model="selectedSchool"
                            track-by="id"
                            label="school_name"
                            :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_ACCOUNT_SELECT.PLACEHOLDER')"
                            :disabled="isSchoolSelectionLocked || isEditingDisabled || isSchoolOptionsLoading"
                            :loading="isSchoolOptionsLoading"
                            selected-label
                            :select-label="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_ACCOUNT_SELECT.SELECT_PLACEHOLDER')"
                            :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_ACCOUNT_SELECT.REMOVE')"
                            :max-height="160"
                            :options="schoolAccountsOptions"
                            :allow-empty="true"
                            :option-height="104"
                            @select="chooseSchool"
                        />
                    </div>
                    <div class="mt-2 flex flex-col gap-2">
                        <label>
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_DOCUMENT.LABEL') }}
                            <input
                                v-model.trim="schoolDocument"
                                type="text"
                                v-mask="'##.###.###/####-##'"
                                disabled
                                autocomplete="off"
                                inputmode="numeric"
                                :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_DOCUMENT.PLACEHOLDER')"
                            />
                        </label>

                        <p class="mt-1 text-xs text-yellow-800 dark:text-yellow-200 bg-yellow-50 dark:bg-yellow-900/40 border border-yellow-200 dark:border-yellow-500 rounded px-3 py-2" role="alert">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_DOCUMENT.CHANGE_SCHOOL_WARNING') }}
                        </p>

                        <label v-if="enrollment && selectedSchool && selectedSchool.extension_session_option_enable" class="flex items-center gap-2">
                            <input v-model.trim="extensionSessionEnable" type="checkbox" />
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_OPTION_ENABLE.LABEL') }}
                        </label>

                        <label v-if="enrollment && generateEnrollmentBillsEnable && allowEnrollmentExemption" class="flex items-center gap-2">
                            <input v-model.trim="enrollmentExempt" type="checkbox" />
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.ENROLLMENT_EXEMPT.LABEL') }}
                        </label>
                    </div>
                </div>
            </fieldset>

            

            <!-- Bloco único: Matrícula (Ano letivo, Nível, Série, Turma e Matrícula) -->
            <fieldset v-if="enrollment" class="form-section" role="group" aria-labelledby="section-matricula">
                <legend id="section-matricula" class="section-title cursor-pointer" @click.prevent="toggleSection('matricula')">
                    <fluent-icon :icon="collapsed.matricula ? 'chevron-right' : 'chevron-down'" size="16" />
                    Matrícula
                </legend>
                <div v-show="!collapsed.matricula">
                    <div :class="{ error: $v.schoolYear.$error }">
                        <label>
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_YEAR.LABEL') }}
                        </label>
                        <multiselect v-model="schoolYear" :disabled="!selectedSchool || isEditingDisabled"
                            :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_YEAR.PLACEHOLDER')" selected-label
                            :select-label="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_YEAR.SELECT_PLACEHOLDER')"
                            :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_YEAR.REMOVE')" :max-height="160"
                            :options="schoolYearOptions" :allow-empty="true"
                            :option-height="104" @input="$v.schoolYear.$touch" @select="chooseSchoolingType" />
                        <span v-if="$v.schoolYear.$error" class="message">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_YEAR.ERROR_MESSAGE') }}
                        </span>
                    </div>

                    <div class="row" :class="{ error: $v.schoolingType.$error }">
                        <label>
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOLING_TYPE.LABEL') }}
                        </label>
                        <multiselect v-model="schoolingType" track-by="VALUE" label="LABEL" :disabled="isEditingDisabled"
                            :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOLING_TYPE.PLACEHOLDER')" selected-label
                            :select-label="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOLING_TYPE.SELECT_PLACEHOLDER')"
                            :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOLING_TYPE.REMOVE')" :max-height="160"
                            :options="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOLING_TYPE.OPTIONS')" :allow-empty="true"
                            :option-height="104" @input="$v.schoolingType.$touch" @select="chooseSchoolingType" />

                        <span v-if="$v.schoolingType.$error" class="message">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOLING_TYPE.ERROR_MESSAGE') }}
                        </span>
                    </div>

                    <div :class="{ error: $v.schoolClass.$error }">
                        <label>
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_CLASS.LABEL') }}
                        </label>
                        <multiselect v-model="schoolClass" :disabled="isEditingDisabled"
                            track-by="id"
                            label="school_class"
                            :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_CLASS.PLACEHOLDER')" selected-label
                            :select-label="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_CLASS.SELECT_PLACEHOLDER')"
                            :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_CLASS.REMOVE')" :max-height="160"
                            :options="schoolClassOptions" :allow-empty="true" @select="chooseSchoolClass"
                            :option-height="104" @input="$v.schoolClass.$touch" />
                        <span v-if="$v.schoolClass.$error" class="message">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_CLASS.ERROR_MESSAGE') }}
                        </span>
                    </div>

                    <div :class="{ error: $v.schoolSection.$error }">
                        <label>
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_SECTION.LABEL') }}
                        </label>
                        <multiselect v-model="schoolSection" :disabled="isEditingDisabled"
                            :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_SECTION.PLACEHOLDER')" selected-label
                            :select-label="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_SECTION.SELECT_PLACEHOLDER')"
                            :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_SECTION.REMOVE')" :max-height="160"
                            :options="schoolClassSectionOption" :allow-empty="true" @select="chooseSchoolSection"
                            :option-height="104" @input="$v.schoolSection.$touch" />
                        <span v-if="$v.schoolSection.$error" class="message">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.SCHOOL_SECTION.ERROR_MESSAGE') }}
                        </span>
                    </div>

                    <label v-if="enrollment || isNewEnrollment" :class="{ error: $v.number.$error }">
                        <div class="field-label-with-action">
                            <span>{{ $t('SCHOOL_CONTACT_FORM.FORM.ENROLLMENT_NUMBER.LABEL') }}</span>
                            <woot-button
                                v-if="isNewEnrollment"
                                variant="link"
                                size="small"
                                icon="search"
                                :isLoading="searchEnrollmentLoading"
                                :disabled="isEditingDisabled"
                                @click.prevent="handleSearchEnrollment"
                            >
                                {{ $t('SCHOOL_CONTACT_FORM.FORM.SEARCH_DEPENDENT.BUTTON_LABEL') }}
                            </woot-button>
                        </div>
                        <input v-model.trim="number" type="text" autocomplete="off"
                            :disabled="!isNewEnrollment || isEditingDisabled"
                            :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.ENROLLMENT_NUMBER.PLACEHOLDER')" @input="$v.number.$touch" />
                        <span v-if="$v.number.$error" class="message">
                            {{ $t('SCHOOL_CONTACT_FORM.FORM.ENROLLMENT_NUMBER.ERROR_MESSAGE') }}
                        </span>
                    </label>
                </div>
            </fieldset>

            <fieldset v-if="enrollment" class="form-section" role="group" aria-labelledby="section-student">
            <legend id="section-student" class="section-title cursor-pointer" @click.prevent="toggleSection('student')">
                <fluent-icon :icon="collapsed.student ? 'chevron-right' : 'chevron-down'" size="16" />
                Aluno
            </legend>
            <div v-show="!collapsed.student">
            <label :class="{ error: $v.studentName.$error }">  
                {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_NAME.LABEL') }}
                <input :disabled="isEditingDisabled" v-model.trim="studentName" type="text" autocomplete="name" :aria-invalid="$v.studentName.$error ? 'true' : 'false'"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_NAME.PLACEHOLDER')" @input="$v.studentName.$touch" />
                <span v-if="$v.studentName.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_NAME.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.studentDateOfBirth.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_DATE_OF_BIRTH.LABEL') }}
                <input v-model.trim="studentDateOfBirth" type="date" :max="todayDate" autocomplete="bday" :aria-invalid="$v.studentDateOfBirth.$error ? 'true' : 'false'"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_DATE_OF_BIRTH.PLACEHOLDER')"
                    @input="$v.studentDateOfBirth.$touch" />
                <span v-if="$v.studentDateOfBirth.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_DATE_OF_BIRTH.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.studentFirstDocument.$error}">
                <div class="field-label-with-action">
                    <span>{{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_FIRST_DOCUMENT.LABEL') }}</span>
                    <woot-button
                        v-if="isNewEnrollment"
                        variant="link"
                        size="small"
                        icon="search"
                        :isLoading="searchStudentDocumentLoading"
                        :disabled="isEditingDisabled"
                        @click.prevent="handleSearchStudentDocument"
                    >
                        {{ $t('SCHOOL_CONTACT_FORM.FORM.SEARCH_DEPENDENT.BUTTON_LABEL') }}
                    </woot-button>
                </div>
                <input v-model.trim="studentFirstDocument" type="text" :disabled="hasStudentFirstDocument || isEditingDisabled" inputmode="numeric" autocomplete="off" :aria-invalid="$v.studentFirstDocument.$error ? 'true' : 'false'"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_FIRST_DOCUMENT.PLACEHOLDER')"
                    @input="$v.studentFirstDocument.$touch" v-mask="'###.###.###-##'" />   
                
                <span v-if="$v.studentFirstDocument.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.STUDENT_FIRST_DOCUMENT.ERROR_MESSAGE') }}
                </span>
            </label>
            </div>
            </fieldset>

            <fieldset v-if="enrollment || isNewEnrollment" class="form-section" role="group" aria-labelledby="section-responsible">
            <legend id="section-responsible" class="section-title cursor-pointer" @click.prevent="toggleSection('responsible')">
                <fluent-icon :icon="collapsed.responsible ? 'chevron-right' : 'chevron-down'" size="16" />
                Identificação do responsável financeiro
            </legend>
            <div v-show="!collapsed.responsible">
            <label :class="{ error: $v.responsibleFirstDocument.$error}">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_FIRST_DOCUMENT.LABEL') }}
                <input v-model.trim="responsibleFirstDocument" type="text" inputmode="numeric" autocomplete="off"
                    :disabled="!!enrollment || isEditingDisabled"
                    :class="{ 'overdue-alert': isDefaulterResponsibleWithKedu }"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_FIRST_DOCUMENT.PLACEHOLDER')"
                    @input="$v.responsibleFirstDocument.$touch" v-mask="'###.###.###-##'" />   
                <div v-if="$v.responsibleFirstDocument.$error" class="message -mt-2 mb-1">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_FIRST_DOCUMENT.ERROR_MESSAGE') }}
                </div>
                <div v-else-if="isDefaulterResponsibleWithSchool" class="message text-yellow-300 -mt-2 mb-1">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.DEFAUTER_IN_SCHOOL_ALERT') }}
                </div>
                <div v-else-if="isDefaulterResponsibleWithKedu" class="message text-yellow-300 -mt-2 mb-1">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.DEFAUTER_IN_KEDU_ALERT') }}
                </div>
                <div v-if="isNewEnrollment && !enrollment" class="medium-12 field-button">
                    <woot-button variant="clear" @click.prevent="lookFinancialOwner" size="small" :isLoading="searchForDependentsLoading" :disabled="isEditingDisabled">
                        {{ this.$t('SCHOOL_CONTACT_FORM.FORM.SEARCH_FOR_DEPENDENTS.BUTTON_LABEL') }}
                    </woot-button>
                </div>
            </label>
            <label :class="{ error: $v.responsibleName.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_NAME.LABEL') }}
                <input v-model.trim="responsibleName" type="text" :disabled="!isNewEnrollment || hasResponsibleName || isEditingDisabled" autocomplete="name" :aria-invalid="$v.responsibleName.$error ? 'true' : 'false'"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_NAME.PLACEHOLDER')"
                    @input="$v.responsibleName.$touch" />
                <span v-if="$v.responsibleName.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_NAME.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.responsiblePhoneNumber.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_PHONE_NUMBER.LABEL') }}
                <input v-model="responsiblePhoneNumber" type="tel" autocomplete="tel" inputmode="tel" :aria-invalid="$v.responsiblePhoneNumber.$error ? 'true' : 'false'"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_PHONE_NUMBER.PLACEHOLDER')"
                    @input="$v.responsiblePhoneNumber.$touch" v-mask="'(##) #########'" />
                <span v-if="$v.responsiblePhoneNumber.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_PHONE_NUMBER.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.responsibleEmail.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_EMAIL.LABEL') }}
                <input v-model="responsibleEmail" type="email" autocomplete="email" :aria-invalid="$v.responsibleEmail.$error ? 'true' : 'false'"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_EMAIL.PLACEHOLDER')"
                    @input="$v.responsibleEmail.$touch" />
                <span v-if="$v.responsibleEmail.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_EMAIL.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.responsibleBirthDate.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_BIRTH_DATE.LABEL') }}
                <input v-model.trim="responsibleBirthDate" type="date" autocomplete="bday" :aria-invalid="$v.responsibleBirthDate.$error ? 'true' : 'false'"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_BIRTH_DATE.PLACEHOLDER')"
                    @input="$v.responsibleBirthDate.$touch" :max="maxBirthDate" />
                <span v-if="$v.responsibleBirthDate.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.CONTRACTOR_BIRTH_DATE.ERROR_MESSAGE') }}
                </span>
            </label>
            </div>
            </fieldset>

            <fieldset v-if="enrollment" class="form-section" role="group" aria-labelledby="section-address">
            <legend id="section-address" class="section-title cursor-pointer" @click.prevent="toggleSection('address')">
                <fluent-icon :icon="collapsed.address ? 'chevron-right' : 'chevron-down'" size="16" />
                Dados pessoais do responsável financeiro
            </legend>
            <div v-show="!collapsed.address">
            <label :class="{ error: $v.responsibleZipCode.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_ZIP_CODE.LABEL') }}
                <input v-model.trim="responsibleZipCode" type="text" inputmode="numeric" autocomplete="postal-code" :aria-invalid="$v.responsibleZipCode.$error ? 'true' : 'false'"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_ZIP_CODE.PLACEHOLDER')"
                    @input="$v.responsibleZipCode.$touch" v-mask="'#####-###'" />
                <span v-if="$v.responsibleZipCode.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_ZIP_CODE.ERROR_MESSAGE') }}
                </span>
                <div class="medium-12 field-button">
                    <woot-button variant="clear" @click.prevent="searchResponsibleZipCode" size="small" :disabled="isEditingDisabled">
                        {{ this.$t('SCHOOL_CONTACT_FORM.FORM.SEARCH_ZIPCODE.BUTTON_LABEL') }}
                    </woot-button>
                </div>
            </label>

            <div class="row" :class="{ error: $v.responsibleState.$error }">
                <label>
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_STATE.LABEL') }}
                </label>
                <multiselect v-model="responsibleState" track-by='ID' label="NAME"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_STATE.PLACEHOLDER')" selected-label
                    :select-label="$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_STATE.SELECT_PLACEHOLDER')"
                    :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_STATE.REMOVE')" :max-height="160"
                    :options="$t('SCHOOL_CONTACT_FORM.FORM.STATE.OPTIONS')" :allow-empty="true" :option-height="104" />
                <span v-if="$v.responsibleState.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_STATE.ERROR_MESSAGE') }}
                </span>
            </div>

            <label :class="{ error: $v.responsibleCity.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_CITY.LABEL') }}
                <input v-model.trim="responsibleCity" type="text" autocomplete="address-level2"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_CITY.PLACEHOLDER')"
                    @input="$v.responsibleCity.$touch" />
                <span v-if="$v.responsibleCity.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_CITY.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.responsibleDistrict.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_DISTRICT.LABEL') }}
                <input v-model.trim="responsibleDistrict" type="text" autocomplete="address-line2"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_DISTRICT.PLACEHOLDER')"
                    @input="$v.responsibleDistrict.$touch" />
                <span v-if="$v.responsibleDistrict.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_DISTRICT.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.responsibleStreet.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_STREET.LABEL') }}
                <input v-model.trim="responsibleStreet" type="text" autocomplete="address-line1"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_STREET.PLACEHOLDER')"
                    @input="$v.responsibleStreet.$touch" />
                <span v-if="$v.responsibleStreet.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_STREET.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.responsibleBuildingNumber.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_BUILDING_NUMBER.LABEL') }}
                <input v-model.trim="responsibleBuildingNumber" type="text" inputmode="text" autocomplete="address-line2"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_BUILDING_NUMBER.PLACEHOLDER')"
                    @input="$v.responsibleBuildingNumber.$touch" />
                <span v-if="$v.responsibleBuildingNumber.$error" class="message">
                    <template v-if="!$v.responsibleBuildingNumber.required">
                        {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_BUILDING_NUMBER.ERROR_MESSAGE') }}
                    </template>
                    <template v-else-if="!$v.responsibleBuildingNumber.keduValidator">
                        {{ $t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_BUILDING_NUMBER.INVALID_MESSAGE') }}
                    </template>
                </span>
            </label>
            </div>
            </fieldset>

            <div v-if="isNewEnrollment && responsibleFirstDocument && dependents && !enrollment" class="row">
                <label>
                {{ $t('SCHOOL_CONTACT_FORM.FORM.DEPENDENTS_SELECT.LABEL') }}
                </label>

                <multiselect v-model="enrollment" track-by="id" label="label"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.DEPENDENTS_SELECT.PLACEHOLDER')" selected-label
                    :select-label="$t('SCHOOL_CONTACT_FORM.FORM.DEPENDENTS_SELECT.SELECT_PLACEHOLDER')"
                    :deselect-label="$t('SCHOOL_CONTACT_FORM.FORM.DEPENDENTS_SELECT.REMOVE')" :max-height="160"
                    :options="dependentOptions" :allow-empty="true" :option-height="104" @select="chooseDependent" />

            </div>

            <fieldset v-if="enrollment && showFinancialFields" class="form-section" role="group" aria-labelledby="section-financial">
            <legend id="section-financial" class="section-title cursor-pointer" @click.prevent="toggleSection('financial')">
                <fluent-icon :icon="collapsed.financial ? 'chevron-right' : 'chevron-down'" size="16" />
                {{ $t('SCHOOL_CONTACT_FORM.FORM.TOTAL_TUITION.LABEL') }}
            </legend>
            <div v-show="!collapsed.financial">
            <label :class="{ error: $v.totalTuition.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.TOTAL_TUITION.LABEL') }}
                <input v-model.trim="totalTuition" type="text"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.TOTAL_TUITION.PLACEHOLDER')" @input="$v.totalTuition.$touch" 
                    v-mask="moneyMask"/> 
                <span v-if="$v.totalTuition.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.TOTAL_TUITION.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.installmentNumber.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENT_NUMBER.LABEL') }}
                <input v-model.trim="installmentNumber" type="number" inputmode="numeric"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENT_NUMBER.PLACEHOLDER')"
                    @input="$v.installmentNumber.$touch" min="1" :max="installmentLimit" step="1" :disabled="isInstallmentLocked || isEditingDisabled"/>
                <span v-if="$v.installmentNumber.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.INSTALLMENT_NUMBER.ERROR_MESSAGE') + installmentLimit + "." }}
                </span>
            </label>

            <label :class="{ error: $v.tuition.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.TUITION.LABEL') }}
                <input v-model.trim="tuition" type="text" disabled
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.TUITION.PLACEHOLDER')" @input="$v.tuition.$touch" 
                    v-mask="moneyMask"/> 
                <span v-if="$v.tuition.$error" class="message">
                        {{ $t('SCHOOL_CONTACT_FORM.FORM.TUITION.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.tuitionDueDate.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.TUITION_DUE_DATE.LABEL') }}
                <input v-model.trim="tuitionDueDate" type="date"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.TUITION_DUE_DATE.PLACEHOLDER')"
                    @input="$v.tuitionDueDate.$touch" :min="tuitionDueDateMin" :max="tuitionDueDateMax" />
                <span v-if="$v.tuitionDueDate.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.TUITION_DUE_DATE.ERROR_MESSAGE') }}
                </span>
            </label>

            <div>
                <input v-model.trim="totalDiscountIsPercentage" type="checkbox" :disabled="isEditingDisabled" />
                <label>
                {{ $t('SCHOOL_CONTACT_FORM.FORM.TOTAL_DISCOUNT_IS_PERCENTAGE.LABEL') }}
                </label>
            </div>

            <label v-if="!totalDiscountIsPercentage" :class="{ error: $v.totalDiscount.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.TOTAL_DISCOUNT.LABEL') }}
                <input v-model.trim="totalDiscount" type="text"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.TOTAL_DISCOUNT.PLACEHOLDER')" @input="$v.totalDiscount.$touch"
                    v-mask="moneyMask">
                <span v-if="$v.totalDiscount.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.TOTAL_DISCOUNT.ERROR_MESSAGE') }}
                </span>
            </label>

            <label v-else :class="{ error: $v.totalDiscountInPercentage.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.TOTAL_DISCOUNT_IN_PERCENTAGE.LABEL') }}
                <input v-model.trim="totalDiscountInPercentage" type="number"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.TOTAL_DISCOUNT_IN_PERCENTAGE.PLACEHOLDER')" @input="$v.totalDiscountInPercentage.$touch"
                    >
                <span v-if="$v.totalDiscountInPercentage.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.TOTAL_DISCOUNT_IN_PERCENTAGE.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.discount.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.DISCOUNT.LABEL') }}
                <input v-model.trim="discount" type="text" disabled
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.DISCOUNT.PLACEHOLDER')" @input="$v.discount.$touch"
                    v-mask="moneyMask">
            </label>

            <label>
                <input v-model.trim="punctualityDiscountRuleEqualToDueDate" type="checkbox" :disabled="isEditingDisabled" />
                {{ $t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_RULE_EQUAL_TO_DUE_DATE.LABEL') }}
            </label>

            <label v-if="!punctualityDiscountRuleEqualToDueDate" :class="{ error: $v.punctualityDiscountRule.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_RULE.LABEL') }}
                <input v-model.trim="punctualityDiscountRule" type="date" :min="todayDate" :max="tuitionDueDate"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_RULE.PLACEHOLDER')"
                    @input="$v.punctualityDiscountRule.$touch" />
                <span v-if="$v.punctualityDiscountRule.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.PUNCTUALITY_DISCOUNT_RULE.ERROR_MESSAGE') }}
                </span>
            </label>

            <label :class="{ error: $v.costCenter.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.COST_CENTER.LABEL') }}
                <input v-model.trim="costCenter" type="text" disabled class="disabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.COST_CENTER.PLACEHOLDER')" @input="$v.costCenter.$touch" />
                <span v-if="$v.costCenter.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.COST_CENTER.ERROR_MESSAGE') }}
                </span>
            </label>

            <label v-if="extensionSessionCheck" :class="{ error: $v.extensionSessionEnrollmentTotalValue.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ENROLLMENT_TOTAL_VALUE.LABEL') }}
                <input v-model.trim="extensionSessionEnrollmentTotalValue" type="text"
                    :disabled="isEditingDisabled"
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ENROLLMENT_TOTAL_VALUE.PLACEHOLDER')" @input="$v.extensionSessionEnrollmentTotalValue.$touch" 
                    v-mask="moneyMask"/> 
                <span v-if="$v.extensionSessionEnrollmentTotalValue.$error" class="message">
                    {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ENROLLMENT_TOTAL_VALUE.ERROR_MESSAGE') }}
                </span>
            </label>

            <label v-if="extensionSessionCheck" :class="{ error: $v.extensionSessionEnrollmentValue.$error }">
                {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ENROLLMENT_VALUE.LABEL') }}
                <input v-model.trim="extensionSessionEnrollmentValue" type="text" disabled
                    :placeholder="$t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ENROLLMENT_VALUE.PLACEHOLDER')" @input="$v.extensionSessionEnrollmentValue.$touch" 
                    v-mask="moneyMask"/> 
                <span v-if="$v.extensionSessionEnrollmentValue.$error" class="message">
                        {{ $t('SCHOOL_CONTACT_FORM.FORM.EXTENSION_SESSION_ENROLLMENT_VALUE.ERROR_MESSAGE') }}
                </span>
            </label>
            </div>
            </fieldset>

            <div v-if="editingBlockedMessage" class="alert-message warning margin-bottom-1">
                <fluent-icon icon="warning" size="16" class="text-yellow-600" />
                <span class="text-sm">{{ editingBlockedMessage }}</span>
            </div>

            <div v-if="enrollment" class="modal-footer">
                <div class="medium-12 columns">
                    <woot-button
                        :isLoading="isSaving"
                        :disabled="inProgress || uiFlags.isCreating || uiFlags.isUpdating || uiFlags.isFetching || isEditingDisabled || isSaving"
                        type="submit"
                    >
                        {{ $t('SCHOOL_CONTACT_FORM.FORM.SUBMIT') }}
                    </woot-button>
                    <button class="button clear" @click.prevent="onCancel">
                        {{ $t('SCHOOL_CONTACT_FORM.FORM.CANCEL') }}
                    </button>
                </div>
            </div>

            <woot-delete-modal
                v-if="showDeleteEnrollmentModal"
                :show.sync="showDeleteEnrollmentModal"
                :on-close="closeDeleteEnrollmentModal"
                :on-confirm="confirmDeleteEnrollment"
                :title="$t('SCHOOL_CONTACT_FORM.FORM.DELETE_ENROLLMENT.CONFIRM.TITLE')"
                :message="$t('SCHOOL_CONTACT_FORM.FORM.DELETE_ENROLLMENT.CONFIRM.MESSAGE')"
                :confirm-text="$t('SCHOOL_CONTACT_FORM.FORM.DELETE_ENROLLMENT.CONFIRM.YES')"
                :reject-text="$t('SCHOOL_CONTACT_FORM.FORM.DELETE_ENROLLMENT.CONFIRM.NO')"
            />

            <school-form-modal  
                v-if="showDefaulterModal"
                :show.sync="showDefaulterModal"
                :title="defaulterModalTitle"
                :message="$t('SCHOOL_CONTACT_FORM.FORM.DEFAUTER_MODAL.MESSAGE')"
                :buttonProps='defaulterModalButtons'
            />

            <school-form-modal
                v-if="showResponsibleMismatchModal"
                :show.sync="showResponsibleMismatchModal"
                :title="responsibleMismatchModalTitle"
                :message="responsibleMismatchModalMessage"
                :buttonProps="responsibleMismatchModalButtons"
            />

            <school-form-modal
                v-if="showActiveContractModal"
                :show.sync="showActiveContractModal"
                :title="activeContractModalTitle"
                :message="$t('SCHOOL_CONTACT_FORM.FORM.ACTIVE_CONTRACT_WARNING')"
                :buttonProps="activeContractModalButtons"
            />
    </form>
</template>
<script>
import { mapGetters } from 'vuex';
import { required, minLength, email, minValue } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import { isKeduFieldValid } from 'shared/helpers/KeduValidators';
import maskMixin from 'shared/mixins/inputMaskMixin';
import zipCodeMixin from 'shared/mixins/zipCodeMixin';
import SchoolEnrollmentApi from 'dashboard/api/kedu/schoolEnrollments.js';
import SchoolFormModal from './SchoolFormModal.vue';

export default {
    components: {
        SchoolFormModal,
    },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        inProgress: {
            type: Boolean,
            default: false,
        },
        selectedSchoolLabels: {
            type: Array,
            default: () => [],
        },
        contractEditStatus: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            showDefaulterModal: false,
            showDeleteEnrollmentModal: false,

            number: '',
            schoolSection: '',
            schoolClass: '',
            schoolingType: '',
            schoolYear: '',
            studentName: '',
            studentFirstDocument: '',
            studentDateOfBirth: '',
            responsibleName: '',
            responsibleZipCode: '',
            responsibleState: '',
            responsibleCity: '',
            responsibleDistrict: '',
            responsibleStreet: '',
            responsibleBuildingNumber: '',
            responsibleFirstDocument: '',
            totalTuition: '',
            tuition: '',
            tuitionDueDate: '',
            installmentNumber: '',
            totalDiscountIsPercentage: false,
            totalDiscountInPercentage: 0,
            totalDiscount: '',
            discount: '',
            punctualityDiscountRule: '',
            costCenter: '',
            schoolDocument: '',

            extensionSessionEnrollmentTotalValue: '',
            extensionSessionEnrollmentValue: '',
            extensionSessionEnable: false,

            responsiblePhoneNumber: '',
            responsibleEmail: '',
            responsibleBirthDate: '',
            responsibleRg: '',
            responsibleOccupation: '',
            responsibleMaritalStatus: '',
            relationshipWithStudent: '',
            studentParentsMaritalStatus: '',

            dependents: null,
            enrollment: null,
            selectedSchool: null,
            isSchoolSelectionLocked: false,
            isNewEnrollment: false,
            searchForDependentsLoading: false,
            showLoadingModal: false,
            isSaving: false,
            overdueBills: null,
            punctualityDiscountRuleEqualToDueDate: false,
            hasStudentFirstDocument: false,
            hasResponsibleName: true,
            enrollmentExempt: false,
            newEnrollmentDependentRefId: null,
            newEnrollmentDependentId: null,
            existingEnrollmentId: null,
            existingEnrollmentRegistry: '',
            existingEnrollmentRefId: null,
            newEnrollmentFinancialOwnerId: null,
            newEnrollmentFinancialOwnerRefId: null,
            searchEnrollmentLoading: false,
            searchStudentDocumentLoading: false,
            showResponsibleMismatchModal: false,
            pendingKeducoreData: null,
            skipStudentAutoFetch: false,
            maxBirthDate: this.getMaxBirthDate(),
            showActiveContractModal: false,
            collapsed: {
                enrollmentSelect: false,
                school: false,
                matricula: false,
                student: false,
                responsible: false,
                address: false,
                financial: false,
            },
        };
    },
    mixins: [alertMixin, maskMixin, zipCodeMixin],
    validations() {
        let financialFields = {
            totalDiscountInPercentage: {},
            totalDiscount: {},
            extensionSessionEnrollmentTotalValue: {},
            extensionSessionEnrollmentValue: {},
            totalTuition: {},
            tuition: {},
            tuitionDueDate: {},
            installmentNumber: {},
            discount: {},
            punctualityDiscountRule: {},
            costCenter: {}
        };

        if (this.showFinancialFields) {
            let totalDiscount = this.totalDiscountIsPercentage
                ? { totalDiscountInPercentage: { required } }
                : { totalDiscount: { required, keduValidator: this.isDiscountNotValid } };

            let extensionSession = this.extensionSessionCheck
                ? {
                    extensionSessionEnrollmentTotalValue: { required },
                    extensionSessionEnrollmentValue: { required }
                }
                : {
                    extensionSessionEnrollmentTotalValue: {},
                    extensionSessionEnrollmentValue: {}
                };

            let punctualityDiscountRule = this.punctualityDiscountRuleEqualToDueDate 
                ? { 
                    punctualityDiscountRule: {}
                } 
                : {
                    punctualityDiscountRule: {
                        required,
                        punctualityDiscountRuleGreaterThanTuitionDueDate: () => !(this.punctualityDiscountRule > this.tuitionDueDate),
                        punctualityDiscountRuleNotBeforeToday: () => !(
                            this.punctualityDiscountRule &&
                            this.todayDate &&
                            this.punctualityDiscountRule < this.todayDate
                        ),
                    }
                };

            financialFields = {
                ...financialFields,
                ...totalDiscount,
                ...extensionSession,
                ...punctualityDiscountRule,
                totalTuition: { required, moneyGreaterThanZero: this.validateMoneyGreaterThanZero },
                tuition: { required, moneyGreaterThanZero: this.validateMoneyGreaterThanZero },
                tuitionDueDate: { required, notRetroactive: () => !(this.tuitionDueDate && this.todayDate && this.tuitionDueDate < this.todayDate) },
                installmentNumber: { required, minValue: minValue(1), keduValidator: () => !this.isInstallmentNotValid },
                discount: { required },
                costCenter: { required }
            };
        }

        let studentFirstDocument = {};
        if (new Date(this.studentDateOfBirth) < new Date('2017-05-11') && (!this.studentFirstDocument || this.studentFirstDocument.length === 0)) {
            studentFirstDocument = {
                studentFirstDocument: {}
            };
        } else {
            studentFirstDocument = {
                studentFirstDocument: {
                    keduValidator: this.checkDataValidation('firstDocument'),
                    firstDocumentsAreTheSame: () => !this.firstDocumentsAreTheSame
                }
            };
        }

        return {
            ...financialFields,
            ...studentFirstDocument,
            number: {},
            schoolClass: { required },
            schoolSection: { required },
            schoolingType: { required },
            schoolYear: { required },
            studentName: { required, minLength: minLength(4), keduValidator: this.checkDataValidation('fullname') },
            studentDateOfBirth: { required },
            responsibleName: { required, keduValidator: this.checkDataValidation('fullname') },
            responsiblePhoneNumber: { required, minLength: minLength(8) },
            responsibleEmail: { required, email },
            responsibleBirthDate: { required, isAdult: () => !(this.responsibleBirthDate > this.maxBirthDate) },
            responsibleZipCode: { required, keduValidator: this.checkDataValidation('zipCode') },
            responsibleState: { required },
            responsibleCity: { required, keduValidator: this.checkDataValidation('city') },
            responsibleDistrict: { required },
            responsibleStreet: { required, keduValidator: this.checkDataValidation('street') },
            responsibleBuildingNumber: { required, keduValidator: this.checkDataValidation('buildingNumber') },
            responsibleFirstDocument: {
                required,
                keduValidator: this.checkDataValidation('firstDocument'),
                firstDocumentsAreTheSame: () => !this.firstDocumentsAreTheSame
            }
        };
    },
    computed: {
        ...mapGetters({
            enrollments: 'schoolEnrollments/getSchoolEnrollments',
            uiFlags: 'schoolEnrollments/getUIFlags',
            getAccount: 'accounts/getAccount',
            accountUIFlags: 'accounts/getUIFlags',
            accountId: 'getCurrentAccountId',
            currentUserId: 'getCurrentUserID',
            currentUser: 'getCurrentUser',
            currentUserRole: 'getCurrentRole',
        }),
        todayDate() {
            // ISO string in YYYY-MM-DD, consistent with other date fields
            return new Date().toISOString().split('T')[0];
        },
        contactId() {
            return this.contact?.id;
        },
        currentAccount() {
            return this.getAccount(this.accountId);
        },
        isSchoolOptionsLoading() {
            return Boolean(this.accountUIFlags?.isFetchingItem);
        },
        schoolAccountsOptions() {
            return this.currentAccount?.school_accounts || [];
        },
        schoolYearOptions() {
            return (this.selectedSchool?.school_class_rules && Array.from(
                new Set(
                    this.selectedSchool?.school_class_rules.map(rule => rule.school_year)
                )
            )) || [];
        },
        schoolClassRulesPerSchoolingType() {
            return this.selectedSchool?.school_class_rules?.filter(option => option.schooling_type === this.schoolingType?.VALUE );
        },
        schoolClasses() {
            return this.schoolClassRulesPerSchoolingType?.filter(schoolClassRule => schoolClassRule.school_year === this.schoolYear);
        },
        schoolClassOptions() {
            return this.schoolClasses || [];
        },
        schoolClassSections(){
            return this.schoolClasses?.find(schoolClass => schoolClass.id === this.schoolClass?.id)?.school_class_sections || [];
        },
        schoolClassSectionOption() {
            return this.schoolClassSections?.map(classSection => classSection.section) || [];
        },
        checkDataValidation() {
            return (dataParams) => (value) => isKeduFieldValid(value, dataParams);
        },
        isDiscountNotValid() {
            if (this.totalTuition && parseFloat(this.totalDiscount) > parseFloat(this.totalTuition)) {
                return true;
            }
            return false;
        },
        isInstallmentNotValid() {
            if (this.installmentNumber && this.installmentNumber > this.installmentLimit) {
                return true;
            }
            return false;
        },
        // Lock parcelamento for 2025 and ensure installments end within the due-date year
        isInstallmentLocked() {
            const currentYear = new Date().getFullYear();
            return parseInt(this.schoolYear, 10) === currentYear;
        },
        installmentLimit() {
            const DEFAULT_LIMIT = 12;
            const currentYear = new Date().getFullYear();
            const selectedYear = parseInt(this.schoolYear, 10);
            const hasSelectedYear = !Number.isNaN(selectedYear);

            if (hasSelectedYear && selectedYear === currentYear) {
                return 1;
            }

            if (!this.tuitionDueDate) {
                return DEFAULT_LIMIT;
            }

            const [dueYear, dueMonth] = this.tuitionDueDate.split('-').map(n => parseInt(n, 10));

            if (Number.isNaN(dueYear) || Number.isNaN(dueMonth)) {
                return DEFAULT_LIMIT;
            }
            console.log('school', this.selectedSchool)
            const isExtended = this.selectedSchool?.allow_installments_after_target_year || this.selectedSchool?.allow_installments_after_selected_year;

            if (isExtended) {
                const targetYear = selectedYear + 1;
                const targetMonth = 1; // Janeiro do próximo ano

                if (dueYear > targetYear || (dueYear === targetYear && dueMonth > targetMonth)) {
                    return 1;
                }

                const monthsRemaining = ((targetYear - dueYear) * 12) + (targetMonth - dueMonth) + 1;
                return Math.max(1, Math.min(DEFAULT_LIMIT, monthsRemaining));
            } else {
                if (hasSelectedYear && dueYear > selectedYear) {
                    return 1;
                }

                const monthsRemaining = 12 - dueMonth + 1;
                return Math.max(1, Math.min(DEFAULT_LIMIT, monthsRemaining));
            }
        },
        tuitionDueDateMin() {
            // Prevent retroactive dates by setting today as the minimum
            return this.todayDate;
        },
        tuitionDueDateMax() {
            if (!this.schoolYear) return null;

            const isExtended = this.selectedSchool?.allow_installments_after_target_year || this.selectedSchool?.allow_installments_after_selected_year;

            if (isExtended) {
                return `${parseInt(this.schoolYear, 10) + 1}-01-31`;
            }

            return `${this.schoolYear}-12-31`;
        },
        dependentOptions(){
            const newDependentOption = {
                label: this.$t('SCHOOL_CONTACT_FORM.FORM.NEW_DEPENDENT_OPTION'),
                isNew: true,
            }

            return this.dependents ? [...this.dependents, newDependentOption] : [newDependentOption];
        },
        contactLabels(){
            return this.$store.getters['contactLabels/getContactLabels'](this.contact?.id);
        },
        isDefaulterResponsibleWithSchool(){
            return !!this.overdueBills?.overdue_school_bills?.length;
        },
        isDefaulterResponsibleWithKedu(){
            return !!this.overdueBills?.overdue_bank_bills?.length;
        },
        defaulterModalTitle(){
            return this.isDefaulterResponsibleWithSchool ? this.$t('SCHOOL_CONTACT_FORM.FORM.DEFAUTER_MODAL.IN_SCHOOL_TITLE') : this.$t('SCHOOL_CONTACT_FORM.FORM.DEFAUTER_MODAL.IN_KEDU_TITLE');
        },
        defaulterModalButtons(){
            return this.isDefaulterResponsibleWithSchool ? this.defaulterWithSchoolButtons : this.defaulterWithKeduButtons;
        },
        defaulterWithSchoolButtons(){
            return [
                {variant: "clear", onClick: this.handleDefaulterSendMessageButton, text: this.$t('SCHOOL_CONTACT_FORM.FORM.DEFAUTER_MODAL.BUTTONS.SEND_DEFAUTER_MESSAGE')},
                {variant: "clear", onClick: this.handleDefaulterCancelButton, text: this.$t('SCHOOL_CONTACT_FORM.FORM.DEFAUTER_MODAL.BUTTONS.CANCEL')}
            ];
        },
        defaulterWithKeduButtons(){
            return [
                {variant: "clear", onClick: this.handleDefaulterSendMessageButton, text: this.$t('SCHOOL_CONTACT_FORM.FORM.DEFAUTER_MODAL.BUTTONS.SEND_DEFAUTER_MESSAGE')},
                {variant: "clear", onClick: this.handleDefaulterContinueButton, text: this.$t('SCHOOL_CONTACT_FORM.FORM.DEFAUTER_MODAL.BUTTONS.CONTINUE')}
            ];
        },
        responsibleMismatchModalTitle() {
            return this.$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_MISMATCH.TITLE');
        },
        responsibleMismatchModalMessage() {
            const payload = this.pendingKeducoreData?.payload;
            if (!payload) {
                return '';
            }

            const studentName = payload.dependent?.student_name || this.studentName || '';
            const responsibleName = payload.responsible?.responsible_name || '';
            const cpf = this.formatCpf(payload.responsible?.responsible_first_document);
            const template = this.$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_MISMATCH.MESSAGE');

            return template
                .replace('{student}', studentName)
                .replace('{responsible}', responsibleName || this.$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_MISMATCH.DEFAULT_RESPONSIBLE'))
                .replace('{cpf}', cpf || '-');
        },
        responsibleMismatchModalButtons() {
            return [
                {
                    variant: "clear",
                    onClick: this.handleResponsibleMismatchConfirm,
                    text: this.$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_MISMATCH.CONFIRM')
                },
                {
                    variant: "clear",
                    onClick: this.handleResponsibleMismatchCancel,
                    text: this.$t('SCHOOL_CONTACT_FORM.FORM.RESPONSIBLE_MISMATCH.CANCEL')
                }
            ];
        },
        conversations() {
            return this.$store.getters['contactConversations/getContactConversation'](
                this.contact?.id
            );
        },
        lastConversation() {
            return this.conversations && this.conversations.length > 0 
                ? this.conversations.reduce((latest, current) => {
                    return new Date(current.created_at) > new Date(latest.created_at) ? current : latest;
                })
                : null;
        },
        firstDocumentsAreTheSame() {
            return this.responsibleFirstDocument && this.studentFirstDocument && 
                   this.responsibleFirstDocument === this.studentFirstDocument;
        },
        extensionSessionCheck() {
            return this.extensionSessionEnable && this.selectedSchool?.extension_session_option_enable;
        },
        generateEnrollmentBillsEnable() {
            return this.selectedSchool ? this.selectedSchool.generate_enrollment_bills_enable : true;
        },
        allowEnrollmentExemption() {
            return this.selectedSchool ? this.selectedSchool.allow_enrollment_exemption : false;
        },
        showFinancialFields() {
            return this.generateEnrollmentBillsEnable && (!this.allowEnrollmentExemption || !this.enrollmentExempt);
        },
        // Disable matrícula fields when a digital acceptance contract is ACCEPTED
        isMatriculaLocked() {
            return !!(this.enrollment && this.enrollment.digital_acceptance_accepted);
        },
        isEnrollmentYearBlocked() {
            return this.isYearLocked(this.enrollment?.school_year);
        },
        // Disable editing when there's a pending digital contract
        isPendingContractBlocking() {
            return this.contractEditStatus && !this.contractEditStatus.can_edit;
        },
        editingBlockedMessage() {
            if (this.isPendingContractBlocking && this.contractEditStatus?.message) {
                return this.contractEditStatus.message;
            }

            if (this.isEnrollmentYearBlocked) {
                return this.getEnrollmentYearLockedMessage();
            }

            return null;
        },
        // Combined check for both blocking conditions
        isEditingDisabled() {
            return this.isMatriculaLocked || this.isPendingContractBlocking || this.isEnrollmentYearBlocked || this.isContractCompleted;
        },
        canDeleteEnrollment() {
            if (!this.enrollment?.id) {
                return false;
            }

            return this.currentUser?.type === 'SuperAdmin' || this.currentUserRole === 'administrator';
        },
        isContractCompleted() {
            return this.enrollment && this.enrollment.contract_status === 'completed';
        },
        hasActiveEnrollment() {
            return this.pendingKeducoreData?.payload?.dependent?.active_enrollment_situation === 'ACTIVE';
        },
        activeContractModalTitle() {
            return 'Contrato Ativo Encontrado';
        },
        activeContractModalButtons() {
            return [
                {
                    variant: "clear",
                    onClick: this.closeActiveContractModal,
                    text: this.$t('SCHOOL_CONTACT_FORM.FORM.CANCEL') // Reusing CANCEL as a generic close
                }
            ];
        }
    },
    mounted() {
        this.fetchLabels();
        if (this.contact?.id) {
            this.$store.dispatch('contactConversations/get', this.contact.id);
        }

        if (this.enrollment && this.enrollment.id && !this.enrollment.isNew) {
            this.$emit('enrollment-selected', this.enrollment.id);
        }
    },
    watch: {
        contactId() {
            this.onCancel();
        },
        enrollment(newEnrollment) {
            // Only emit when a real enrollment is selected, ignore synthetic options
            if (newEnrollment && newEnrollment.id && newEnrollment.id !== '__new__' && !newEnrollment.isNew) {
                this.$emit('enrollment-selected', newEnrollment.id);
            }
        },
        totalDiscount(val) {
            // Guard against reactive loops by avoiding self-assignments with same values
            const tuitionDec = this.moneyMaskToDecimal(this.totalTuition);
            const limitNum = Math.floor(tuitionDec * 90) / 100;
            const limitStr = limitNum.toFixed(2);
            const parsedVal = this.moneyMaskToDecimal(val);

            if (this.totalTuition === '') {
                // When total tuition is empty, don't override totalDiscount repeatedly
            } else if (parsedVal > limitNum) {
                if (parsedVal !== limitNum) {
                    this.totalDiscount = limitStr;
                }
            }

            const installments = parseInt(this.installmentNumber, 10) || 1;
            const denom = installments < 1 ? 1 : installments;
            const nextDiscount = `${this.roundMoneyToFloor(this.moneyMaskToDecimal(this.totalDiscount) / denom)}`;
            if (this.discount !== nextDiscount) {
                this.discount = nextDiscount;
            }
        },

        totalDiscountInPercentage(val) {
            let pct = Number(val) || 0;
            if (pct > 90) {
                pct = 90;
                if (this.totalDiscountInPercentage !== 90) {
                    this.totalDiscountInPercentage = 90;
                }
            }
            const nextTotalDiscount = `${this.roundMoneyToFloor(this.moneyMaskToDecimal(this.totalTuition) * pct / 100)}`;
            if (this.totalDiscount !== nextTotalDiscount) {
                this.totalDiscount = nextTotalDiscount;
            }
            const installments = parseInt(this.installmentNumber, 10) || 1;
            const denom = installments < 1 ? 1 : installments;
            const nextDiscount = `${this.roundMoneyToFloor(this.moneyMaskToDecimal(this.totalDiscount) / denom)}`;
            if (this.discount !== nextDiscount) {
                this.discount = nextDiscount;
            }
        },

        totalDiscountIsPercentage() {
            const nextTotalDiscount = `${this.roundMoneyToFloor(this.moneyMaskToDecimal(this.totalTuition) * this.totalDiscountInPercentage / 100)}`;
            if (this.totalDiscount !== nextTotalDiscount) {
                this.totalDiscount = nextTotalDiscount;
            }
            const installments = parseInt(this.installmentNumber, 10) || 1;
            const denom = installments < 1 ? 1 : installments;
            const nextDiscount = `${this.roundMoneyToFloor(this.moneyMaskToDecimal(this.totalDiscount) / denom)}`;
            if (this.discount !== nextDiscount) {
                this.discount = nextDiscount;
            }
        },
        
        installmentNumber(val) {
            let next = parseInt(val, 10);
            if (Number.isNaN(next)) next = 0;
            if (next > this.installmentLimit) next = this.installmentLimit;
            if (next < 1) next = 1;
            if (this.installmentNumber !== next) {
                this.installmentNumber = next;
            }
            const installments = parseInt(this.installmentNumber, 10) || 1;
            const denom = installments < 1 ? 1 : installments;
            const nextTuition = `${this.roundMoneyToFloor(this.moneyMaskToDecimal(this.totalTuition) / denom)}`;
            const nextDiscount = `${this.roundMoneyToFloor(this.moneyMaskToDecimal(this.totalDiscount) / denom)}`;
            if (this.tuition !== nextTuition) {
                this.tuition = nextTuition;
            }
            if (this.discount !== nextDiscount) {
                this.discount = nextDiscount;
            }
        },
        totalTuition(val) {
            const installments = parseInt(this.installmentNumber, 10) || 1;
            const denom = installments < 1 ? 1 : installments;
            const nextTuition = `${this.roundMoneyToFloor(this.moneyMaskToDecimal(val) / denom)}`;
            if (this.tuition !== nextTuition) {
                this.tuition = nextTuition;
            }

            if (this.totalDiscountIsPercentage) {
                const nextTotalDiscount = `${this.roundMoneyToFloor(this.moneyMaskToDecimal(val) * this.totalDiscountInPercentage / 100)}`;
                if (this.totalDiscount !== nextTotalDiscount) {
                    this.totalDiscount = nextTotalDiscount;
                }
                const nextDiscount = `${this.roundMoneyToFloor(this.moneyMaskToDecimal(this.totalDiscount) / denom)}`;
                if (this.discount !== nextDiscount) {
                    this.discount = nextDiscount;
                }
            }
        },
        schoolYear(val){
            this.costCenter = `KEDU ONE MATRICULA ${val || ''}`;
            // Reset due date only if it exceeds the end of the selected year
            if (this.tuitionDueDate) {
                const [y] = this.tuitionDueDate.split('-');
                if (parseInt(y, 10) > parseInt(val, 10)) {
                    this.tuitionDueDate = '';
                }
            }

            const currentYear = new Date().getFullYear();
            if (parseInt(val, 10) === currentYear && this.installmentNumber !== 1) {
                this.installmentNumber = 1;
            }
            // clamp installments to new limit
            if (this.installmentNumber && this.installmentNumber > this.installmentLimit) {
                this.installmentNumber = this.installmentLimit;
            }
        },
        tuitionDueDate(val) {
            if (!val || !this.schoolYear) return;
            const [y, m] = val.split('-').map(n => parseInt(n, 10));
            
            const isExtended = this.selectedSchool?.allow_installments_after_target_year || this.selectedSchool?.allow_installments_after_selected_year;
            const maxYear = isExtended ? parseInt(this.schoolYear, 10) + 1 : parseInt(this.schoolYear, 10);
            const maxMonth = isExtended ? 1 : 12;

            if (y > maxYear || (y === maxYear && m > maxMonth)) {
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.FINANCIAL_PLAN.YEAR_LIMIT_ERROR'));
                this.tuitionDueDate = '';
                return;
            }
            
            if (this.installmentNumber && this.installmentNumber > this.installmentLimit) {
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.FINANCIAL_PLAN.YEAR_LIMIT_ERROR'));
                this.installmentNumber = this.installmentLimit;
            }
        },
        extensionSessionEnrollmentTotalValue(val) {
            const installments = parseInt(this.installmentNumber, 10) || 1;
            const denom = installments < 1 ? 1 : installments;
            const nextValue = `${this.roundMoneyToFloor(this.moneyMaskToDecimal(val) / denom)}`;
            if (this.extensionSessionEnrollmentValue !== nextValue) {
                this.extensionSessionEnrollmentValue = nextValue;
            }
        },
        studentFirstDocument(val){
            if (!this.isNewEnrollment) {
                return;
            }

            if (this.skipStudentAutoFetch) {
                this.skipStudentAutoFetch = false;
                return;
            }

            if (val && !this.$v.studentFirstDocument.$invalid) {
                this.searchDependentDocument({ studentDocument: val, notifyOnEmpty: false });
            }
        }
    },
    methods: {
        getYearBadgeClass(year) {
            if (!year) return 'year-badge';
            const yearNumber = parseInt(year, 10);
            const colorIndex = yearNumber % 5;
            return `year-badge year-badge--${colorIndex}`;
        },
        isYearLocked(year) {
            if (!year) {
                return false;
            }

            const currentYear = new Date().getFullYear();
            const enrollmentYear = parseInt(String(year).trim(), 10);

            return enrollmentYear < currentYear;
        },
        getEnrollmentYearLockedMessage() {
            const key = 'SCHOOL_CONTACT_FORM.FORM.ENROLLMENT_YEAR_LOCKED';
            const message = this.$t(key);
            return message === key ? 'Você está consultando um contrato do ano anterior.' : message;
        },
        lockSchoolSelection() {
            this.isSchoolSelectionLocked = true;
        },
        unlockSchoolSelection() {
            this.isSchoolSelectionLocked = false;
        },
        toggleSection(section) {
            this.$set(this.collapsed, section, !this.collapsed[section]);
        },
        normalizeDateString(rawValue) {
            if (!rawValue || typeof rawValue !== 'string') {
                return '';
            }

            const trimmed = rawValue.trim();

            if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
                return trimmed.slice(0, 10);
            }

            if (/^\d{2}\/\d{2}\/\d{4}/.test(trimmed)) {
                const datePart = trimmed.split(' ')[0];
                const [dd, mm, yyyy] = datePart.split('/');
                if (dd && mm && yyyy) {
                    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
                }
            }

            return '';
        },
        async fetchDependentFromKeducore({
            studentDocument = null,
            enrollmentNumber = null,
            loadingFlag = null,
            notifyOnEmpty = true,
            emptyFieldKey = null,
            successMessageKey = null,
        } = {}) {
            if (!this.isNewEnrollment) {
                return false;
            }

            if (!this.schoolDocument) {
                if (notifyOnEmpty) {
                    this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.SEARCH_DEPENDENT.MISSING_SCHOOL'));
                }
                return false;
            }

            const hasDocument = !!studentDocument;
            const hasEnrollment = !!enrollmentNumber;

            if (!hasDocument && !hasEnrollment) {
                if (notifyOnEmpty && emptyFieldKey) {
                    this.showAlert(this.$t(emptyFieldKey));
                }
                return false;
            }

            if (loadingFlag) {
                this[loadingFlag] = true;
            }

            try {
                this.showResponsibleMismatchModal = false;
                this.pendingKeducoreData = null;

                const response = await SchoolEnrollmentApi.searchDependent(this.contact.id, {
                    schoolDocument: this.schoolDocument,
                    studentFirstDocument: studentDocument,
                    enrollmentNumber,
                });
                const data = response?.data || {};

                if (data.dependent) {
                    return this.processKeducorePayload(data, { notifyOnEmpty, successMessageKey });
                }

                if (notifyOnEmpty) {
                    this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.SEARCH_DEPENDENT.NOT_FOUND_MESSAGE'));
                }

                return false;
            } catch (error) {
                if (notifyOnEmpty) {
                    this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.SEARCH_DEPENDENT.ERROR_MESSAGE'));
                }

                return false;
            } finally {
                if (loadingFlag) {
                    this[loadingFlag] = false;
                }
            }
        },
        applyKeducoreDependentData(payload = {}, options = {}) {
            const { preserveResponsibleData = false } = options;
            if (!payload || typeof payload !== 'object') {
                return;
            }

            const { dependent, responsible, keducore } = payload;

            if (dependent) {
                this.hasStudentFirstDocument = !!dependent.student_first_document;

                if (dependent.student_name) {
                    this.studentName = dependent.student_name;
                }

                if (dependent.student_first_document) {
                    this.skipStudentAutoFetch = true;
                    this.studentFirstDocument = dependent.student_first_document;
                }

                const normalizedBirth = this.normalizeDateString(dependent.student_birth_date);
                if (normalizedBirth) {
                    this.studentDateOfBirth = normalizedBirth;
                }

                if (this.isNewEnrollment) {
                    const registry = dependent.active_enrollment_registry || dependent.number;
                    if (registry) {
                        this.number = registry;
                        this.existingEnrollmentRegistry = registry;
                    }
                }

                if (dependent.parents_marital_status) {
                    this.studentParentsMaritalStatus = dependent.parents_marital_status;
                }

                if (dependent.relationship_with_student) {
                    this.relationshipWithStudent = dependent.relationship_with_student;
                }

                if (dependent.custody_arrangement) {
                    this.relationshipWithStudent = dependent.custody_arrangement;
                }

                if (this.isNewEnrollment) {
                    this.lockSchoolSelection();
                }
            }

            if (responsible && !preserveResponsibleData) {
                this.hasResponsibleName = !!responsible.responsible_name;

                if (responsible.responsible_name) {
                    this.responsibleName = responsible.responsible_name;
                }

                if (responsible.responsible_first_document) {
                    this.responsibleFirstDocument = responsible.responsible_first_document;
                }

                const normalizedBirth = this.normalizeDateString(responsible.responsible_birth_date);
                if (normalizedBirth) {
                    this.responsibleBirthDate = normalizedBirth;
                }

                if (responsible.responsible_phone_number) {
                    this.responsiblePhoneNumber = responsible.responsible_phone_number;
                }

                if (responsible.responsible_email) {
                    this.responsibleEmail = responsible.responsible_email;
                }

                if (responsible.responsible_zip_code) {
                    this.responsibleZipCode = responsible.responsible_zip_code;
                }

                if (responsible.responsible_state) {
                    const option = this.$t('SCHOOL_CONTACT_FORM.FORM.STATE.OPTIONS').find(state => state.ID === responsible.responsible_state);
                    this.responsibleState = option || '';
                }

                if (responsible.responsible_city) {
                    this.responsibleCity = responsible.responsible_city;
                }

                if (responsible.responsible_district) {
                    this.responsibleDistrict = responsible.responsible_district;
                }

                if (responsible.responsible_street) {
                    this.responsibleStreet = responsible.responsible_street;
                }

                if (responsible.responsible_building_number) {
                    this.responsibleBuildingNumber = responsible.responsible_building_number;
                }

                if (responsible.responsible_rg) {
                    this.responsibleRg = responsible.responsible_rg;
                }

                if (responsible.responsible_occupation) {
                    this.responsibleOccupation = responsible.responsible_occupation;
                }

                if (responsible.responsible_marital_status) {
                    this.responsibleMaritalStatus = responsible.responsible_marital_status;
                }
            }

            this.applyKeducoreIdentifiers({ dependent, keducore });
        },
        async handleSearchEnrollment() {
            if (!this.number) {
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.SEARCH_DEPENDENT.EMPTY_FIELD_ENROLLMENT'));
                return;
            }

            await this.fetchDependentFromKeducore({
                enrollmentNumber: this.number,
                loadingFlag: 'searchEnrollmentLoading',
                emptyFieldKey: 'SCHOOL_CONTACT_FORM.FORM.SEARCH_DEPENDENT.EMPTY_FIELD_ENROLLMENT',
                successMessageKey: 'SCHOOL_CONTACT_FORM.FORM.SEARCH_DEPENDENT.SUCCESS_MESSAGE',
            });
        },
        async handleSearchStudentDocument() {
            if (!this.studentFirstDocument) {
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.SEARCH_DEPENDENT.EMPTY_FIELD_DOCUMENT'));
                return;
            }

            await this.fetchDependentFromKeducore({
                studentDocument: this.studentFirstDocument,
                loadingFlag: 'searchStudentDocumentLoading',
                emptyFieldKey: 'SCHOOL_CONTACT_FORM.FORM.SEARCH_DEPENDENT.EMPTY_FIELD_DOCUMENT',
                successMessageKey: 'SCHOOL_CONTACT_FORM.FORM.SEARCH_DEPENDENT.SUCCESS_MESSAGE',
            });
        },
        async searchDependentDocument({ studentDocument = null, notifyOnEmpty = true } = {}) {
            return this.fetchDependentFromKeducore({
                studentDocument,
                notifyOnEmpty,
            });
        },
        processKeducorePayload(payload, { notifyOnEmpty = true, successMessageKey = null } = {}) {
            // First, check for responsible mismatch
            if (this.shouldPromptResponsibleMismatch(payload)) {
                this.pendingKeducoreData = { payload, options: { notifyOnEmpty, successMessageKey } };
                this.showResponsibleMismatchModal = true;
                return null;
            }

            // Then, check for active contract
            // Temporarily store payload to check for active enrollment, without triggering mismatch modal again
            this.pendingKeducoreData = { payload }; 
            if (this.hasActiveEnrollment) {
                this.showActiveContractModal = true;
                return null;
            }

            this.applyKeducoreDependentData(payload);

            if (notifyOnEmpty && successMessageKey) {
                this.showAlert(this.$t(successMessageKey));
            }

            return true;
        },
        applyKeducoreIdentifiers({ dependent, keducore } = {}, { preserveRegistry = false, preserveFinancialOwner = false } = {}) {
            if (keducore) {
                if (Object.prototype.hasOwnProperty.call(keducore, 'dependent_id')) {
                    this.newEnrollmentDependentId = keducore.dependent_id;
                }

                if (Object.prototype.hasOwnProperty.call(keducore, 'dependent_ref_id')) {
                    this.newEnrollmentDependentRefId = keducore.dependent_ref_id;
                }

                if (Object.prototype.hasOwnProperty.call(keducore, 'enrollment_id')) {
                    this.existingEnrollmentId = keducore.enrollment_id;
                }

                if (Object.prototype.hasOwnProperty.call(keducore, 'enrollment_ref_id')) {
                    this.existingEnrollmentRefId = keducore.enrollment_ref_id;
                }

                if (preserveFinancialOwner) {
                    this.newEnrollmentFinancialOwnerId = null;
                    this.newEnrollmentFinancialOwnerRefId = null;
                } else {
                    if (Object.prototype.hasOwnProperty.call(keducore, 'financial_owner_id')) {
                        this.newEnrollmentFinancialOwnerId = keducore.financial_owner_id;
                    }

                    if (Object.prototype.hasOwnProperty.call(keducore, 'financial_owner_ref_id')) {
                        this.newEnrollmentFinancialOwnerRefId = keducore.financial_owner_ref_id;
                    }
                }
            }

            if (dependent) {
                const registry = dependent.active_enrollment_registry || dependent.number;
                if (registry && (!preserveRegistry || !this.existingEnrollmentRegistry)) {
                    this.existingEnrollmentRegistry = registry;
                }
            }
        },
        shouldPromptResponsibleMismatch(payload = {}) {
            const keducoreDocument = this.sanitizeDocument(payload?.responsible?.responsible_first_document);
            const currentDocument = this.sanitizeDocument(this.responsibleFirstDocument);

            if (!keducoreDocument || !currentDocument) {
                return false;
            }

            return keducoreDocument !== currentDocument;
        },
        sanitizeDocument(document) {
            return document ? document.toString().replace(/\D/g, '') : '';
        },
        formatCpf(document) {
            const sanitized = this.sanitizeDocument(document);
            return sanitized ? this.cpfMask(sanitized) : '';
        },
        handleResponsibleMismatchConfirm() {
            if (this.pendingKeducoreData?.payload) {
                const { payload, options } = this.pendingKeducoreData;
                this.applyKeducoreDependentData(payload, { preserveResponsibleData: true });
                this.applyKeducoreIdentifiers(payload, { preserveRegistry: true, preserveFinancialOwner: true });

                const { notifyOnEmpty, successMessageKey } = options || {};
                if (notifyOnEmpty && successMessageKey) {
                    this.showAlert(this.$t(successMessageKey));
                }
            }

            this.closeResponsibleMismatchModal();
        },
        handleResponsibleMismatchCancel() {
            if (this.pendingKeducoreData?.payload) {
                this.applyKeducoreDependentData(this.pendingKeducoreData.payload);

                const { notifyOnEmpty, successMessageKey } = this.pendingKeducoreData.options || {};
                if (notifyOnEmpty && successMessageKey) {
                    this.showAlert(this.$t(successMessageKey));
                }
            }

            this.closeResponsibleMismatchModal();
        },
        closeResponsibleMismatchModal() {
            this.showResponsibleMismatchModal = false;
            this.pendingKeducoreData = null;
        },
        getMaxBirthDate() {
            const currentDate = new Date();
            const maxBirthDate = new Date(
                currentDate.getFullYear() - 18,
                currentDate.getMonth(),
                currentDate.getDate()
            );
            return maxBirthDate.toISOString().split('T')[0];
        },
        onCancel() {
            this.enrollment = null;
            this.isNewEnrollment = false;
            this.dependents = null;
            this.overdueBills = null;
            this.selectedSchool = null;
            this.searchForDependentsLoading = false;
            this.hasStudentFirstDocument = false;
            this.showDeleteEnrollmentModal = false;
            this.showResponsibleMismatchModal = false;
            this.pendingKeducoreData = null;
            this.skipStudentAutoFetch = false;
            this.unlockSchoolSelection();

            this.number = '';
            this.schoolClass = '';
            this.schoolSection = '';
            this.schoolingType = '';
            this.schoolYear = '';
            this.studentName = '';
            this.studentFirstDocument = '';
            this.studentDateOfBirth = '';
            this.responsibleName = '';
            this.responsibleZipCode = '';
            this.responsibleState = '';
            this.responsibleCity = '';
            this.responsibleDistrict = '';
            this.responsibleStreet = '';
            this.responsibleBuildingNumber = '';
            this.responsibleFirstDocument = '';
            this.tuition = '';
            this.totalTuition = '';
            this.tuitionDueDate = '';
            this.installmentNumber = '';
            this.discount = '';
            this.totalDiscountIsPercentage = false;
            this.totalDiscountInPercentage = 0;
            this.totalDiscount = '';
            this.punctualityDiscountRule = '';
            this.costCenter = '';
            this.schoolDocument = '';

            this.responsiblePhoneNumber = '';
            this.responsibleEmail = '';

            this.extensionSessionEnrollmentValue = '';
            this.extensionSessionEnrollmentTotalValue = '';
            this.extensionSessionEnable = false;
            this.enrollmentExempt = false;
            this.newEnrollmentDependentRefId = null;
            this.newEnrollmentDependentId = null;
            this.existingEnrollmentId = null;
            this.existingEnrollmentRegistry = '';
            this.existingEnrollmentRefId = null;
            this.newEnrollmentFinancialOwnerId = null;
            this.newEnrollmentFinancialOwnerRefId = null;
            this.searchEnrollmentLoading = false;
            this.searchStudentDocumentLoading = false;
            this.responsibleRg = '';
            this.responsibleOccupation = '';
            this.responsibleMaritalStatus = '';
            this.relationshipWithStudent = '';
            this.studentParentsMaritalStatus = '';
        },
        closeActiveContractModal() {
            this.showActiveContractModal = false;
            this.pendingKeducoreData = null;
        },
        newEnrollmentButtonToggle() {
            this.isNewEnrollment = true;
            this.unlockSchoolSelection();

            if (this.schoolAccountsOptions?.length === 1){
                this.selectedSchool = this.schoolAccountsOptions[0];
                this.schoolDocument = this.schoolAccountsOptions[0]?.school_document;
                this.lockSchoolSelection();
            }
        },
        enrollmentPayload() {
            let financialFields = {
                tuition: null,
                total_tuition: null,
                tuition_due_date: null,
                installment_number: null,
                total_discount_is_percentage: null,
                total_discount: null,
                discount: null,
                punctuality_discount_rule: null,
                cost_center: null,
                extension_session_enrollment_total_value: null,
                extension_session_enrollment_value: null,
            }

            if(this.showFinancialFields){
                financialFields = {
                    tuition: this.moneyMaskToDecimal(this.tuition),
                    total_tuition: this.moneyMaskToDecimal(this.totalTuition),
                    tuition_due_date: this.tuitionDueDate,
                    installment_number: this.installmentNumber,
                    total_discount_is_percentage: this.totalDiscountIsPercentage,
                    total_discount: this.totalDiscountIsPercentage ? this.totalDiscountInPercentage : this.moneyMaskToDecimal(this.totalDiscount),
                    discount: this.moneyMaskToDecimal(this.discount),
                    punctuality_discount_rule: this.punctualityDiscountRuleEqualToDueDate ? this.tuitionDueDate : this.punctualityDiscountRule,
                    cost_center: this.costCenter,
                    extension_session_enrollment_total_value: this.selectedSchool?.extension_session_option_enable && this.extensionSessionEnable ? this.moneyMaskToDecimal(this.extensionSessionEnrollmentTotalValue) : null,
                    extension_session_enrollment_value: this.selectedSchool?.extension_session_option_enable && this.extensionSessionEnable ? this.moneyMaskToDecimal(this.extensionSessionEnrollmentValue) : null,
                }
            }

            return ({
                ...financialFields,
                contactId: this.contact.id,
                number: this.number,

                school_class: this.schoolClass.school_class,
                student_school_class: this.schoolClass.school_class,
                
                schooling_type: this.schoolingType.VALUE,
                student_schooling_type: this.schoolingType.VALUE,

                school_year: this.schoolYear,
                school_section: this.schoolSection,

                student_name: this.studentName,
                contract_student_name: this.studentName,

                student_first_document: this.studentFirstDocument,
                contract_student_first_document: this.studentFirstDocument,

                student_date_of_birth: this.studentDateOfBirth,
                contractor_birth_date: this.responsibleBirthDate,
                responsible_birthday: this.responsibleBirthDate,

                responsible_name: this.responsibleName,
                contractor_name: this.responsibleName,
                responsible_phone_number: this.responsiblePhoneNumber,
                contractor_phone_number: this.responsiblePhoneNumber,
                responsible_email: this.responsibleEmail,
                contractor_email: this.responsibleEmail,
                responsible_zip_code: this.responsibleZipCode,
                contractor_zip_code: this.responsibleZipCode,
                responsible_state: this.responsibleState.ID,
                contractor_state: this.responsibleState.ID,
                responsible_city: this.responsibleCity,
                contractor_city: this.responsibleCity,
                responsible_district: this.responsibleDistrict,
                contractor_district: this.responsibleDistrict,
                responsible_street: this.responsibleStreet,
                responsible_building_number: this.responsibleBuildingNumber,
                contractor_address: (this.responsibleStreet && this.responsibleBuildingNumber) 
                    ? (this.responsibleStreet + ", " + this.responsibleBuildingNumber) 
                    : (this.responsibleStreet || this.responsibleBuildingNumber || ''),

                responsible_first_document: this.responsibleFirstDocument,

                enrollment_school_document: this.schoolDocument,
                extension_session_enable: this.extensionSessionEnable,

                user_id: this.currentUserId,
                
                student_schooling_type: this.schoolingType.VALUE,
                enrollment_exempt: this.enrollmentExempt
            });
        },
        async updateEnrollment() {
            const payload = this.enrollmentPayload();
            
            const keducoreData = this.enrollment?.custom_attributes?.keducore;
            if (keducoreData) {
                payload.keducore = keducoreData;
            }
            
            this.showLoadingModal = true;
            try {
                await this.$store.dispatch('schoolEnrollments/update', { id: this.enrollment.id, ...payload });
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.EDIT_ENROLLMENT.SUCCESS_MESSAGE'));
                this.onCancel();
            } catch (error) {
                if(error?.cause?.number){
                    this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.ENROLLMENT_NUMBER.UNIQUENESS_ERROR_MESSAGE'));
                } else if(error?.cause?.student_first_document){
                    this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.ENROLLMENT_NUMBER.STUDENT_DOCUMENT_UNIQUENESS_ERROR_MESSAGE'));
                } else if(error?.cause?.school_year){
                    const msg = error?.cause?.school_year?.[0];
                    if (msg) {
                        this.showAlert(msg);
                    } else {
                        this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.ACTIVE_ENROLLMENT_FOR_YEAR'));
                    }
                } else if(error?.cause?.tuition_due_date || error?.cause?.installment_number){
                    const msg = error?.cause?.installment_number?.[0] || error?.cause?.tuition_due_date?.[0];
                    this.showAlert(msg);
                } else {
                    // Fallback to API-provided message when available (e.g., KeduCore)
                    const msg = error?.message || error?.response?.data?.message;
                    if (msg) {
                        this.showAlert(msg);
                    } else {
                        this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.EDIT_ENROLLMENT.ERROR_MESSAGE'));
                    }
                }
            } finally {
                this.showLoadingModal = false;
            }
        },
        async createEnrollment() {
            const schoolSection = this.schoolClassSections?.find(section => section.section === this.schoolSection);
            const contractParams = {
                annuity: schoolSection?.contract_annuity,
                installment_quantity: schoolSection?.contract_installment_quantity,
                installment_value: schoolSection?.contract_installment_value,
                punctuality_discount_percentage: schoolSection?.contract_punctuality_discount_is_percentage && schoolSection?.contract_punctuality_discount,
                punctuality_discount_value: !schoolSection?.contract_punctuality_discount_is_percentage && schoolSection?.contract_punctuality_discount,
                withdrawal_fee_percentage: schoolSection?.contract_withdrawal_fee_is_percentage && schoolSection?.contract_withdrawal_fee,
                withdrawal_fee_value: !schoolSection?.contract_withdrawal_fee_is_percentage && schoolSection?.contract_withdrawal_fee
            }

            const keducorePayload = {};
            if (this.newEnrollmentDependentRefId) {
                keducorePayload.dependent_ref_id = this.newEnrollmentDependentRefId;
            }
            if (this.newEnrollmentDependentId) {
                keducorePayload.dependent_id = this.newEnrollmentDependentId;
            }
            if (this.newEnrollmentFinancialOwnerId) {
                keducorePayload.financial_owner_id = this.newEnrollmentFinancialOwnerId;
            }
            if (this.newEnrollmentFinancialOwnerRefId) {
                keducorePayload.financial_owner_ref_id = this.newEnrollmentFinancialOwnerRefId;
            }
            const payload = { 
                ...contractParams,
                ...this.enrollmentPayload(), 
                keducore: keducorePayload
            };

            this.showLoadingModal = true;
            try {
                await this.$store.dispatch('schoolEnrollments/create', payload);
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.NEW_ENROLLMENT.SUCCESS_MESSAGE'));
                this.onCancel();
            } catch (error) {
                if(error?.cause?.errors?.number){
                    const msg = error?.cause?.errors?.number?.[0];
                    this.showAlert(msg);
                } else if(error?.cause?.errors?.student_first_document){
                    this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.ENROLLMENT_NUMBER.STUDENT_DOCUMENT_UNIQUENESS_ERROR_MESSAGE'));
                } else if(error?.cause?.errors?.tuition_due_date || error?.cause?.errors?.installment_number){
                    const msg = error?.cause?.errors?.installment_number?.[0] || error?.cause?.errors?.tuition_due_date?.[0];
                    if (msg && msg.includes('a vista')) {
                        this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.FINANCIAL_PLAN.A_VISTA_DEC_PREV_YEAR_ERROR'));
                    } else {
                        this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.FINANCIAL_PLAN.YEAR_LIMIT_ERROR'));
                    }
                }  else {
                    // Fallback to API-provided message when available (e.g., KeduCore)
                    const msg = error?.message || error?.response?.data?.message;
                    if (msg) {
                        this.showAlert(msg);
                    } else {
                        this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.NEW_ENROLLMENT.ERROR_MESSAGE'));
                    }
                }
            } finally {
                this.showLoadingModal = false;
            }
        },
        async lookFinancialOwner() {
            this.showLoadingModal = true;
            try {
                await this.searchOverdueBill();
                if(!this.enrollment) await this.searchForDependents();
            } catch (error) {
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.FETCH_DEPENDENTS_ERROR'));
            } finally {
                this.showLoadingModal = false;
            }
        },
        async searchForDependents() {
            const response = await SchoolEnrollmentApi.searchForDependents(this.contact.id, this.schoolDocument, this.responsibleFirstDocument);

            const { dependents, responsible } = response.data;

            this.responsibleName = responsible?.responsible_name || '';
            this.hasResponsibleName = !!responsible?.responsible_name;

            this.responsibleZipCode = responsible?.responsible_zip_code || '';
            this.responsibleState = (responsible?.responsible_state && this.$t('SCHOOL_CONTACT_FORM.FORM.STATE.OPTIONS').find(option => responsible.responsible_state === option.ID)) || '';
            this.responsibleCity = responsible?.responsible_city || '';
            this.responsibleDistrict = responsible?.responsible_district || '';
            this.responsibleStreet = responsible?.responsible_street || '';
            this.responsibleBuildingNumber = responsible?.responsible_building_number || '';

            // Optional contact info from KeduCore custom fields
            this.responsiblePhoneNumber = responsible?.responsible_phone_number || '';
            this.responsibleEmail = responsible?.responsible_email || '';

            // Normalize birth date to YYYY-MM-DD for date input compatibility
            this.responsibleBirthDate = this.normalizeDateString(responsible?.responsible_birth_date);
            this.responsibleRg = responsible?.responsible_rg || '';
            this.responsibleOccupation = responsible?.responsible_occupation || '';
            this.responsibleMaritalStatus = responsible?.responsible_marital_status || '';
            this.relationshipWithStudent = responsible?.relationship_with_student || '';
            this.studentParentsMaritalStatus = responsible?.parents_marital_status || '';

            this.newEnrollmentDependentRefId = null;
            this.newEnrollmentDependentId = null;
            this.existingEnrollmentId = null;
            this.existingEnrollmentRegistry = '';
            this.existingEnrollmentRefId = null;
            this.newEnrollmentFinancialOwnerId = null;
            this.newEnrollmentFinancialOwnerRefId = null;

            this.dependents = dependents?.map(dependent => ({
                // Ensure a stable id for vue-multiselect track-by
                id: dependent?.id || dependent?.ref_id || dependent?.student_first_document || dependent?.number,
                ...dependent,
                label: dependent.student_name,
                student_birth_date: this.normalizeDateString(dependent?.student_birth_date) || '',
            })) || [];
        },
        async searchOverdueBill() {
            const payload = {
                contact_id: this.contact.id,
                student_name: this.studentName,
                student_first_document: this.studentFirstDocument,
                responsible_name: this.responsibleName,
                responsible_first_document: this.responsibleFirstDocument,
                school_document: this.schoolDocument,
                school_name: this.selectedSchool?.school_name,
                user_id: this.currentUserId
            }

            const response = await SchoolEnrollmentApi.searchOverdueBill(payload);
            this.overdueBills = response.data;

            if(this.isDefaulterResponsibleWithKedu){
                this.showDefaulterModal = true;
            }
        },

        async handleSearchOverdueBill() {
            this.showLoadingModal = true;
            try {
                await this.searchOverdueBill();
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.SEARCH_OVERDUE_BILL.SUCCESS_MESSAGE'));
            } catch (error) {
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.SEARCH_OVERDUE_BILL.ERROR_MESSAGE'));
            } finally {
                this.showLoadingModal = false;
            }
        },

        async syncWithKeducore() {
            this.showLoadingModal = true;
            try {
                const response = await SchoolEnrollmentApi.syncWithKeducore(this.contact.id, this.enrollment.id);
                const {dependent, financial_owner} = response.data;

                this.studentName = dependent.name;
                this.skipStudentAutoFetch = true;
                this.studentFirstDocument = this.cpfMask(dependent.cpf);
                if (dependent.enrollments && dependent.enrollments.length > 0)
                    this.number = dependent.enrollments[0].registry;
                this.responsibleName = financial_owner.name;
                this.responsibleFirstDocument = this.cpfMask(financial_owner.cpf);

                const address = financial_owner.address;

                this.responsibleZipCode = address.zipCode || '';
                this.responsibleState = (address.state && this.$t('SCHOOL_CONTACT_FORM.FORM.STATE.OPTIONS').find(option => address.state === option.ID)) || '';

                this.responsibleCity = address.city || '';
                this.responsibleDistrict = address.district || '';
                this.responsibleStreet = address.street || '';
                this.responsibleBuildingNumber = address.number || '';

                
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.SYNC_WITH_KECUORE.SUCCESS_MESSAGE'));
            } catch (error) {
                console.log(error);
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.SYNC_WITH_KECUORE.ERROR_MESSAGE'));
            } finally {
                this.showLoadingModal = false;
            }
        },

        openDeleteEnrollmentModal() {
            if (!this.canDeleteEnrollment) {
                return;
            }

            this.showDeleteEnrollmentModal = true;
        },
        closeDeleteEnrollmentModal() {
            this.showDeleteEnrollmentModal = false;
        },
        async confirmDeleteEnrollment() {
            if (!this.enrollment?.id) {
                this.closeDeleteEnrollmentModal();
                return;
            }

            if (!this.contact?.id) {
                this.closeDeleteEnrollmentModal();
                return;
            }

            try {
                await this.$store.dispatch('schoolEnrollments/delete', {
                    contactId: this.contact.id,
                    id: this.enrollment.id,
                });
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.DELETE_ENROLLMENT.SUCCESS_MESSAGE'));
                this.$emit('enrollment-selected', null);
                this.onCancel();
            } catch (error) {
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.DELETE_ENROLLMENT.ERROR_MESSAGE'));
            } finally {
                this.closeDeleteEnrollmentModal();
            }
        },

        async handleSubmitEnrollmentForm() {
            if (this.isEditingDisabled || this.isSaving) {
                return;
            }

            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }
            this.isSaving = true;
            try {
                if (this.isNewEnrollment) {
                    await this.createEnrollment();
                } else {
                    await this.updateEnrollment();
                }
            } finally {
                this.isSaving = false;
            }
        },

        chooseEnrollment(selectedEnrollment) {
            this.isNewEnrollment = false;
            this.schoolDocument = selectedEnrollment.enrollment_school_document;
            this.selectedSchool = this.currentAccount?.school_accounts?.find(school => school.school_document === this.schoolDocument);
            if (this.selectedSchool) {
                this.lockSchoolSelection();
            }
            this.enrollmentExempt = selectedEnrollment?.enrollment_exempt;
        
            this.number = selectedEnrollment.number || '';
            let schoolClass = this.selectedSchool?.school_class_rules?.find(schoolClass => schoolClass.id === selectedEnrollment.custom_attributes?.keducore?.academic_serie_id);
            
            this.schoolClass = schoolClass || {};
            this.schoolingType = this.$t('SCHOOL_CONTACT_FORM.FORM.STUDENT_SCHOOLING_TYPE.OPTIONS').find(option => selectedEnrollment.schooling_type === option.VALUE) || '';
            this.schoolYear = selectedEnrollment.school_year || '';
            this.schoolSection = selectedEnrollment.school_section || '';
            this.studentName = selectedEnrollment.student_name || '';

            this.skipStudentAutoFetch = true;
            this.studentFirstDocument = selectedEnrollment.student_first_document || '';
            this.hasStudentFirstDocument = !!selectedEnrollment.student_first_document;

            this.studentDateOfBirth = selectedEnrollment.school_contract?.student_date_of_birth || '';

            this.responsibleName = selectedEnrollment.responsible_name || '';
            this.responsiblePhoneNumber = selectedEnrollment.responsible_phone_number || '';
            this.responsibleEmail = selectedEnrollment.responsible_email || '';
            this.responsibleBirthDate = selectedEnrollment.responsible_birthday ||
                (selectedEnrollment.school_contract && selectedEnrollment.school_contract.contractor_birth_date) || '';
            this.responsibleZipCode = selectedEnrollment.responsible_zip_code || '';
            this.responsibleState = this.$t('SCHOOL_CONTACT_FORM.FORM.STATE.OPTIONS').find(option => selectedEnrollment.responsible_state === option.ID) || '';

            this.responsibleCity = selectedEnrollment.responsible_city || '';
            this.responsibleDistrict = selectedEnrollment.responsible_district || '';
            this.responsibleStreet = selectedEnrollment.responsible_street || '';
            this.responsibleBuildingNumber = selectedEnrollment.responsible_building_number || '';
            this.responsibleFirstDocument = selectedEnrollment.responsible_first_document || '';

            this.tuition = selectedEnrollment.tuition && parseFloat(selectedEnrollment.tuition).toFixed(2) || '';
            this.totalTuition = selectedEnrollment.total_tuition && parseFloat(selectedEnrollment.total_tuition).toFixed(2) || '';
            this.tuitionDueDate = selectedEnrollment.tuition_due_date || '';
            this.installmentNumber = selectedEnrollment.installment_number || '';

            this.extensionSessionEnable = selectedEnrollment.extension_session_enable;
            this.extensionSessionEnrollmentTotalValue = selectedEnrollment?.extension_session_enrollment_total_value && parseFloat(selectedEnrollment.extension_session_enrollment_total_value).toFixed(2) || '';
            this.extensionSessionEnrollmentValue = selectedEnrollment?.extension_session_enrollment_value && parseFloat(selectedEnrollment.extension_session_enrollment_value).toFixed(2) || '';

            if (selectedEnrollment.total_discount_is_percentage) {
                this.totalDiscountIsPercentage = true;
                this.totalDiscountInPercentage = selectedEnrollment.total_discount || '';
            } else {
                this.totalDiscountIsPercentage = false;
                this.totalDiscount = selectedEnrollment.total_discount && parseFloat(selectedEnrollment.total_discount).toFixed(2) || '';
            }
     
            this.discount = selectedEnrollment.discount && parseFloat(selectedEnrollment.discount).toFixed(2) || '';
            this.punctualityDiscountRule = selectedEnrollment.punctuality_discount_rule || '';
            this.costCenter = selectedEnrollment.cost_center;
            this.punctualityDiscountRuleEqualToDueDate = (selectedEnrollment.punctuality_discount_rule === selectedEnrollment.tuition_due_date);

            this.$emit('enrollment-selected', selectedEnrollment.id);
        },

        chooseDependent(selectedDependent) {
            // If user chose the synthetic "Adicionar novo dependente" option,
            // clear any KeduCore linkage identifiers and keep school selection locked
            if (selectedDependent && selectedDependent.isNew) {
                this.newEnrollmentDependentRefId = null;
                this.newEnrollmentDependentId = null;
                this.existingEnrollmentId = null;
                this.existingEnrollmentRegistry = '';
                this.existingEnrollmentRefId = null;
                this.newEnrollmentFinancialOwnerId = null;
                this.newEnrollmentFinancialOwnerRefId = null;
                this.lockSchoolSelection();
                return;
            }
            const {label, ...enrollment} = selectedDependent;

            this.newEnrollmentDependentRefId = selectedDependent.ref_id;
            this.newEnrollmentDependentId = selectedDependent.id || null;
            this.existingEnrollmentId = selectedDependent.active_enrollment_id || null;
            this.existingEnrollmentRegistry = selectedDependent.active_enrollment_registry || '';
            this.existingEnrollmentRefId = selectedDependent.active_enrollment_ref_id || null;
            this.newEnrollmentFinancialOwnerId = null;
            this.newEnrollmentFinancialOwnerRefId = null;
            this.studentName = enrollment.student_name || '';
            this.skipStudentAutoFetch = true;
            this.studentFirstDocument = enrollment.student_first_document || '';
            this.hasStudentFirstDocument = !!enrollment.student_first_document;
            this.studentDateOfBirth = selectedDependent.student_birth_date || '';
            if (selectedDependent.relationship_with_student) {
                this.relationshipWithStudent = selectedDependent.relationship_with_student;
            }
            if (selectedDependent.parents_marital_status) {
                this.studentParentsMaritalStatus = selectedDependent.parents_marital_status;
            }

            this.number = selectedDependent.active_enrollment_registry || enrollment.number || '';

            this.lockSchoolSelection();
        },

        chooseSchool(selectedSchool) {
            this.schoolDocument = selectedSchool?.school_document;
            this.schoolSection = null;
            this.schoolClass = null;
            this.schoolingType = null;
            this.schoolYear = null;
            if (selectedSchool) {
                this.lockSchoolSelection();
            }
        },

        chooseSchoolingType(){
            this.schoolClass = null;
            this.schoolSection = null;
        },

        chooseSchoolClass(schoolClass){
            this.schoolSection = null;
            const section = this.schoolClasses?.find(c => c.school_class === schoolClass);
            if (section) {
                this.totalTuition = section?.enrollment_total_tuition ? parseFloat(section?.enrollment_total_tuition).toFixed(2) : this.totalTuition;
                this.installmentNumber = section?.enrollment_installment_number;
                this.tuition = section?.enrollment_tuition ? parseFloat(section?.enrollment_tuition).toFixed(2) : this.tuition;
                this.totalDiscountIsPercentage = section?.enrollment_total_discount_is_percentage;

                if (section?.enrollment_total_discount_is_percentage) {
                    this.totalDiscountInPercentage = section?.enrollment_total_discount ? section?.enrollment_total_discount : this.totalDiscountInPercentage;
                } else {
                    this.totalDiscount = section?.enrollment_total_discount ? parseFloat(section?.enrollment_total_discount).toFixed(2) : this.totalDiscount;
                }
                
                this.discount = section?.enrollment_discount ? parseFloat(section?.enrollment_discount).toFixed(2) : this.discount;
            }
        },

        chooseSchoolSection(schoolSection) {
            // const section = this.schoolClassSections?.find(section => section.section === schoolSection);

            // if (section) {
            //     this.totalTuition = section?.enrollment_total_tuition ? parseFloat(section?.enrollment_total_tuition).toFixed(2) : this.totalTuition;
            //     this.installmentNumber = section?.enrollment_installment_number;
            //     this.tuition = section?.enrollment_tuition ? parseFloat(section?.enrollment_tuition).toFixed(2) : this.tuition;
            //     this.totalDiscountIsPercentage = section?.enrollment_total_discount_is_percentage;

            //     if (section?.enrollment_total_discount_is_percentage) {
            //         this.totalDiscountInPercentage = section?.enrollment_total_discount ? section?.enrollment_total_discount : this.totalDiscountInPercentage;
            //     } else {
            //         this.totalDiscount = section?.enrollment_total_discount ? parseFloat(section?.enrollment_total_discount).toFixed(2) : this.totalDiscount;
            //     }
                
            //     this.discount = section?.enrollment_discount ? parseFloat(section?.enrollment_discount).toFixed(2) : this.discount;
            // }
        },

        async fetchLabels() {
            if (!this.contact?.id) {
                return;
            }
            this.$store.dispatch('contactLabels/get', this.contact.id);
        },
        validateMoneyGreaterThanZero(moneyValue) {
            if (this.moneyMaskToDecimal(moneyValue) <= 0) {
                return false 
            } 
            return true;
        },

        roundMoneyToFloor(value) {
            return (Math.floor(value * 100) / 100).toFixed(2);
        },

        async searchResponsibleZipCode () {
            if (!this.responsibleZipCode) {
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.SEARCH_ZIPCODE.EMPTY_ZIPCODE_ERROR'));
                return;
            }
            
            try {
                const response = await this.fetchZipCode(this.responsibleZipCode);
                const {data} = response;

                this.responsibleState = (data.uf && this.$t('SCHOOL_CONTACT_FORM.FORM.STATE.OPTIONS').find(option => option.ID === data.uf)) || '';
                this.responsibleCity = data.localidade || '';
                this.responsibleDistrict = data.bairro || '';
                this.responsibleStreet = data.logradouro || '';
            } catch (error) {
                this.showAlert(this.$t('SCHOOL_CONTACT_FORM.FORM.SEARCH_ZIPCODE.API_ERROR'));
            }
        },
        handleDefaulterSendMessageButton() {
            this.showDefaulterModal = false;
            this.onCancel();
            this.sendDefaulterMessage();
        },
        handleDefaulterCancelButton() {
            this.showDefaulterModal = false;
            this.onCancel();
        },
        handleDefaulterContinueButton() {
            this.showDefaulterModal = false;
        },
        async sendDefaulterMessage() {           
            const messagePayload = {
                conversationId: this.lastConversation.id,
                message: this.$t('SCHOOL_CONTACT_FORM.FORM.DEFAUTER_MODAL.DEFAULTER_MESSAGE_IN_CONVERSATION'),
                sender: {
                    name: this.currentUser.name,
                    thumbnail: this.currentUser.avatar_url,
                },
            };
            try {
                await this.$store.dispatch('createPendingMessageAndSend', messagePayload);
            } catch (error) {
                const errorMessage =
                error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
                this.showAlert(errorMessage);
            }
        },
    }
}

</script>
<styles scoped lang="scss">
.contact--form {
    padding: var(--space-normal) var(--space-large) var(--space-large);
    min-height: 200px;

    .columns {
        padding: 0 var(--space-smaller);
    }
}

.field-button {
    margin-top: -10px;
    display: flex;
    justify-content: end;
}

.field-label-with-action {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-smaller);
}

.add-button-area {
    display: flex;
    align-items: flex-end;
    padding-left: 5px;
    padding-bottom: 0px;
}

.add-button {
    margin-bottom: 2px;
}

.enrollment-title {
    margin-bottom: 8px;
}

.year-badge {
    display: inline-flex;
    align-items: center;
    padding: 0 6px;
    height: 20px;
    border-radius: 10px;
    background: var(--color-woot);
    color: var(--white);
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
}

.year-badge--0 {
    background-color: #007bff !important;
}
.year-badge--1 {
    background-color: #28a745 !important;
}
.year-badge--2 {
    background-color: #17a2b8 !important;
}
.year-badge--3 {
    background-color: #20c997 !important;
}
.year-badge--4 {
    background-color: #0056b3 !important;
}

.overdue-alert {
    @apply border-yellow-300 dark:border-yellow-300
}

/* New layout helpers */
.form-section {
    @apply bg-white dark:bg-slate-800 rounded-lg border border-solid border-slate-100 dark:border-slate-700 p-4 mb-4;
}

.section-title {
    @apply text-slate-800 dark:text-slate-100 font-semibold text-xs md:text-sm mb-3 flex items-center gap-2 uppercase tracking-wide;
}

.grid-2 {
    @apply grid grid-cols-1 md:grid-cols-2 gap-4;
}

.grid-3 {
    @apply grid grid-cols-1 md:grid-cols-3 gap-4;
}

.label-text {
    @apply text-slate-700 dark:text-slate-200 text-sm font-medium;
}

.loading-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.loading-content {
    background: white;
    padding: var(--space-large);
    border-radius: var(--border-radius-medium);
    text-align: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 200px;
}

.spinner {
    border: 4px solid var(--color-border);
    border-top: 4px solid var(--color-woot);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto var(--space-small);
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Sticky footer actions for long forms */
.modal-footer {
    @apply sticky bottom-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-t border-solid border-slate-100 dark:border-slate-700 mt-2 pt-3 pb-3;
}

/* Alert message styles for contract blocking */
.alert-message {
    @apply flex items-center gap-2 p-3 rounded border;
}

.alert-message.warning {
    @apply bg-yellow-50 border-yellow-200 text-yellow-800;
}
</styles>
